import { Component } from '@angular/core';
import { ActivatedRoute, Router, NavigationStart, NavigationExtras } from '@angular/router';
import { Subscription } from 'rxjs';
import { FormControl, FormGroup } from '@angular/forms';
import { Location } from '@angular/common';

// campfire
import { ConfigService, PartnerBrandService } from '@campfire-nx/api';
import { LoggerService } from '@campfire-nx/api';
import { AnalyticsService } from '@campfire-nx/api';

// services
import { HelpService } from '@open-portal-app/help/services/help.service';
import { AuthService } from '@open-portal-app/core/services/auth.service';
import { PolicyDetailsService } from '@open-portal-app/core/services/policy-details.service';

import { AddressPipe } from '@open-portal-app/core/pipes/address.pipe';

// constants
import { HELP_CONSTANTS } from '@open-portal-app/help/constants/help.constants';

@Component({
  selector: 'app-help-flow',
  templateUrl: './help-flow.component.html',
  styleUrls: ['./help-flow.component.scss']
})
export class HelpFlowComponent {
  helpFlow: any;
  allQuestionSteps: any;
  activeHelpStep: any;
  helpQuestion: any;
  helpServiceSubscription!: Subscription;
  form!: FormGroup;
  loading: boolean = false;
  token: any;
  queryParams = {
    subject: '',
    'customer-intent': ''
  } as { [key: string]: any };

  isAuthenticated!: boolean;
  userData!: any;
  userSlug!: string | undefined;
  policies!: any[];
  // Policies eligible for the selected support option
  eligiblePolicies!: any[];
  claims!: any[];
  hasOverdueInvoices!: boolean;
  private navigationSubscription!: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private helpService: HelpService,
    private authService: AuthService,
    public partnerBrandService: PartnerBrandService,
    private loggerService: LoggerService,
    private analyticsService: AnalyticsService,
    private policyDetailsService: PolicyDetailsService,
    private configService: ConfigService,
    private addressPipe: AddressPipe
  ) {}

  ngOnInit(): void {
    this.helpServiceSubscription = this.helpService.observeHelp
      .subscribe(() => { this.setupHelpQuestion(); });
    this.route.queryParams
      .subscribe(params => {
        this.queryParams = {
          ...this.queryParams,
          ...params
        }
      });
    this.isAuthenticated = this.authService.isAuthenticated();
    this.userData = this.route.snapshot.data.userData;

    let logdata = {
      partner: this.partnerBrandService.data.partner.slug,
      isAuthenticated: this.isAuthenticated,
      userSlug: null
    };
    this.token = this.authService.getToken();

    if (this.isAuthenticated) {
      this.loading = true;
      let currentEventLogic = 'getUserPolicies';

      this.getUserEntityList('policy')
        .then(policies => {
          this.policies = policies;
          currentEventLogic = 'getUserClaims';

          // grab the user slug from the first policy for logging
          if (this.policies && this.policies.length > 0) {
            const policies = [] as any[];

            Object.keys(this.userData.policies).forEach(key => {
              this.userData.policies[key].forEach((policy: any) => {
                policies.push(policy);
              });
            });
            logdata.userSlug = policies[0].user_slug || policies[0].user.uuid;
          }

          return this.getUserEntityList('claim');
        })
        .then((claims) => {
          this.claims = claims;
          this.hasOverdueInvoices = this.policies?.some(policy => policy.overdueInvoices && policy.overdueInvoices.count > 0);
          currentEventLogic = 'setupHelpQuestion';

          return this.setupHelpQuestion();
        })
        .then(() => {
          this.loading = false;

          this.analyticsService.trackEvent('initiate', 'helpflow', ' ', {}, logdata);
          this.loggerService.info('HelpFlowComponent', 'ngOnInit', logdata);
        })
        .catch((err) => {
          this.loading = false;
          this.loggerService.error('HelpFlowComponent', 'ngOnInit', { error: err }, currentEventLogic);
        });
    } else {
      this.loading = true;
      this.setupHelpQuestion();
      this.loading = false;

      this.analyticsService.trackEvent('initiate', 'helpflow', ' ', {}, logdata);
      this.loggerService.info('HelpFlowComponent', 'ngOnInit', logdata);
    }

    window.addEventListener('beforeunload', this.removeHelpFlowSessionStorage);

    this.navigationSubscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        // we want to reset the help steps and sessionStorage
        this.helpService.resetHelpflow();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.helpServiceSubscription) {
      this.helpServiceSubscription.unsubscribe();
    }

    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }

    window.removeEventListener('beforeunload', this.removeHelpFlowSessionStorage);
  }

  setupHelpQuestion() {
    this.loading = true;
    setTimeout(() => {
      // Fetch entire help flow state info
      this.helpFlow = this.helpService.getHelpflow();

      this.manageAuthentication();

      this.setupHelpQuestionData();

      if (this.activeHelpStep && this.activeHelpStep.type === 'question') {
        this.form = new FormGroup({});
        this.form.addControl(
          this.helpQuestion.key,
          new FormControl(this.helpQuestion.data !== (null || undefined) ? this.helpQuestion.data : '')
        );
      }

      this.loading = false;
    }, 0);
  }

  setupHelpQuestionData() {
    this.helpService.prePopulateSupportSteps(this.queryParams);

    this.activeHelpStep = this.helpFlow.steps[this.helpFlow.activeStepIndex];

    // Filter all question type steps. helps to relate with last question's answer
    this.allQuestionSteps = this.helpFlow.steps.filter((step: { type: string; }) => {
      return step.type === 'question';
    });

    // Fetch question details for active help step
    this.helpQuestion = HELP_CONSTANTS.step_questions.find(step => {
      return this.activeHelpStep ? step.key === this.activeHelpStep.slug : null
    });

    // This is only required when a help question is prepared for a question type step
    if (this.helpQuestion) {
      this.helpQuestion.data = this.activeHelpStep && this.activeHelpStep.answer;

      if (this.activeHelpStep.type === 'question') {
        // Finding the index of last question.
        //  Used to formulate the key for finding current question's choices
        const helpQuestionIndex = this.allQuestionSteps.findIndex((step: any) => this.activeHelpStep.slug === step.slug);

        // Finding the choices for the current help question
        let choicesKey: string;
        if (this.helpFlow.activeStepIndex > 0) {
          choicesKey = this.activeHelpStep.slug +
          (this.allQuestionSteps && this.allQuestionSteps[helpQuestionIndex - 1] ?
            '_' + this.allQuestionSteps[helpQuestionIndex - 1].answer : '');
        } else {
          choicesKey = this.activeHelpStep.slug;
        }

        let questionChoices: any = HELP_CONSTANTS.step_options.filter(options => {
          return options.key === choicesKey;
        });

        if (questionChoices && questionChoices.length> 0) {
          // hide policy and claims choices based on the user's data
          if (this.isAuthenticated) {
            questionChoices[0].choices = questionChoices[0].choices.filter((choice: any) => {
              return this.checkChoiceEligibility(choice);
            });
          } else {
            // Hide these options for unauthenticated user: SFD, coverage check
            questionChoices[0].choices = questionChoices[0].choices.filter((choice: any) => {
              return (!choice.requiresTravelPolicyWithSfd && ['odo', 'coverage'].indexOf(choice.key) === -1 )
            });
          }

          // remove login help choice if already logged in
          if (this.isAuthenticated && questionChoices[0].key === 'customer-intent_other') {
            questionChoices[0].choices = questionChoices[0].choices.filter((choice: any) => choice.key !== 'login-help');
          }

          // hiding retry payment choice if no overdue invoices or user only has travel policies
          if (!this.hasOverdueInvoices || (this.policies && this.hasOnlyTravelPolicy(this.policies))) {
            questionChoices[0].choices = questionChoices[0].choices.filter((choice: any) => choice.key !== 'retry-payment');
          }

          // hiding Manage policy renewal choice if user only has travel policies
          if (this.policies && this.hasOnlyTravelPolicy(this.policies)) {
            questionChoices[0].choices = questionChoices[0].choices.filter((choice: any) => choice.key !== 'update-renewal');
          }

          // Check user's policies if odo readings are required, hide the option if not needed
          if (this.policies && this.helpService.hasPaydPolicy(this.policies)) {
            this.helpService.getOdoRequiredPolicies(this.policies)
              .then((odoRequiredPolicies: any[]) => {
                if (odoRequiredPolicies && !odoRequiredPolicies.length) {
                  // Remove odometer option if payd policies already have odo readings
                  questionChoices[0].choices = questionChoices[0].choices.filter((choice: any) => choice.key !== 'odo');
                  this.helpQuestion.settings.choices = questionChoices[0].choices || [];
                }
              })
              .catch((err) => {
                this.loading = false;
                this.loggerService.error('HelpFlowComponent', 'getOdoRequiredPolicies', { error: err });
              });
          } else {
            questionChoices[0].choices = questionChoices[0].choices.filter((choice: any) => choice.key !== 'odo');
          }

          this.helpQuestion.settings.choices = questionChoices[0].choices || [];
        }
      } else if (this.activeHelpStep.type === 'list') {
        this.filterEligiblePoliciesForSelectedSupport();
      }
    }
  }

  getUserEntityList(entityType: 'policy' | 'claim'): Promise<any[]> {
    return new Promise(async (resolve, reject) => {
      let entityList: any[] = [];

      let pluraliseEntityType = entityType === 'policy' ? 'policies' : `${entityType}s`;
      for (const [key, value] of Object.entries(this.userData && this.userData[pluraliseEntityType])) {
        let entity: any = value;

        switch(entityType) {
          case 'policy':
            if (entity && entity.length > 0) {
              const promises = entity.map((item: any) => this.getFormattedPolicy(item));
              const results = await Promise.all(promises);
              if (results && results.length > 0 && results[0]) {
                entityList.push(...results);
              }
            }
            break;
          case 'claim':
            const formattedClaim = this.getFormattedClaim(entity);
            entityList.push(formattedClaim);
            break;
        }
      }
      resolve(entityList);
    });
  }

  async getFormattedPolicy(policy: any) {

    return this.policyDetailsService.formatPolicy(policy)
      .then((response: any) => {
        return {
          key: response.slug,
          label: this.getCoveredLabel(policy.product.class, response),
          value: {
            slug: response.slug,
            class: policy.product?.class
          },
          icon: this.getIcon(policy.product?.class),
          policyNumber: response.policyNumber,
          coveredEntity: this.getCoveredEntity(policy.product.class, response),
          action: 'flow',
          class: policy.product?.class,
          status: response?.policyState,
          policyTypeLabel: response?.policyTypeLabel,
          overdueInvoices: response?.overdueInvoices,
          coverType: response.coverType,
          coverLevel: response.coverLevel,
          startDate: response.startDate
        };
      })
      .catch((err) => {
        this.loggerService.error('DashboardComponent', 'getFormattedPolicy', { error: err });
      })
  }

  getFormattedClaim(claim: any) {
    const { claim_number, event_type, product, policy_slug, product_type, slug, state } = claim;
    const claimTypes: { [key: string]: { label: string } } = this.configService.data.types.claimTypes[claim.product_type];
    const policyDetails = this.policies.find((policy: any) => policy && policy.key === policy_slug);

    let icon = '';
    let label = '-';

    switch (product_type) {
      case 'C':
        icon = 'management-damagecar-small'; break;
      case 'H':
        icon = 'management-damagehouse-small'; break;
      case 'T':
        icon = 'travel-small'; break;
      default:
        break;
    }

    if (claimTypes) {
      for (const [key, value] of Object.entries(claimTypes)) {
        const event = this.configService.data.types.claimEventTypes[key][event_type];
        if (event) {
          label = value.label;
          break;
        }
      }
    } else {
      label = '';
    }

    return {
      key: slug,
      value: {
        class: product.class,
        slug
      },
      class: product.class,
      status: state,
      action: 'flow',
      coveredEntity: policyDetails && policyDetails.coveredEntity,
      claimNumber: `Claim number: ${claim_number}`,
      icon,
      label
    };
  }

  getIcon(productClass: string) {
    switch (productClass) {
      case 'car':
        return 'car-yourcar';
      case 'home':
        return 'house-home';
      case 'travel':
        return 'travel-holiday';
      case 'mobile':
        return 'marketing-optionals';
      default:
        return 'answer-notsure';
    }
  }

  getCoveredEntity(productClass: string, policyData: any) {
    let entity = '';

    if (productClass === 'car') {
      entity = policyData.helpflowCarDisplayName;
    } else if (productClass === 'home') {
      entity = !policyData.isRaptorial? (this.addressPipe.transform(policyData.address)) : policyData.address;
    }

    return entity;
  }

  getCoveredLabel(productClass: string, policyData: any) {
    if (productClass === 'mobile') return policyData.deviceModel;
    return policyData.policyTypeLabel;
  }

  goBack() {
    const subject = this.helpService.getAnswerByStepSlug('subject');
    const referenceAnswer = this.helpService.getAnswerByStepSlug('reference-list');

    if (this.helpFlow.activeStepIndex === 0 && this.partnerBrandService.data.partner.redirectBack) {
        // handle navigation from handovers
        window.open(this.partnerBrandService.data.partner.redirectBack, '_self');
    } else if (this.helpFlow.activeStepIndex === 0 && !this.partnerBrandService.data.partner.redirectBack) {
      // handle navigation from within app
      this.router.navigate(['/dashboard']);
    } else if (subject === 'quote' && referenceAnswer?.slug) {
      // handle navigation from within app
      this.location.back();
    } else {
      // navigate within support flow steps
      this.helpService.back();
    }
  }

  removeHelpFlowSessionStorage() {
    window.sessionStorage.removeItem('helpflow');
  }

  // Returns true if user at least 1 active policy
  hasActiveUpcomingPolicy(policies: any, statuses: Array<string>) {
    let activePolicies =  policies.filter((policy: any) => {
      return statuses.indexOf(policy.status) > -1;
    });

    return activePolicies.length > 0;
  }

  // Returns true if user at least 1 Raptor travel policy which can have Smart Flight Delay
  hasTravelPolicyWithSfd(policies: any) {
    let policiesWithSfd =  policies.filter((policy: any) => {
      return policy.class === 'travel' &&
        policy.status === 'active' &&
        policy.coverType === 'int' &&
        policy.coverLevel === 'comp'
    });

    return policiesWithSfd.length > 0;
  }

  // Returns true if user ONLY has Raptor travel policy
  hasOnlyTravelPolicy(policies: any) {
    let nonTravelPolicies =  policies.filter((policy: any) => {
      return policy.class !== 'travel'
    });

    return nonTravelPolicies.length === 0;
  }


  // Hide certain policies in policies list step
  //  Policies are hidden if they are ineligible for selected Support options
  filterEligiblePoliciesForSelectedSupport() {
    // Reset policies list with eligible policies
    this.eligiblePolicies = this.policies?.filter((policy: any) => {
      // Travel policy is NOT supported for following selections:
      //  1. Manage my payments --> Retry my payment
      //  2. Manage my policy --> Manage my policy renewal
      // checking helpFlow.steps for customer-intent(index 1)
      let retryPaymentSelected = this.helpFlow && this.helpFlow.steps[1].answer === 'retry-payment';
      let updateRenewalSelected = this.helpFlow && this.helpFlow.steps[1].answer === 'update-renewal';

      return policy?.class !== 'travel' || (!retryPaymentSelected && !updateRenewalSelected);
    });
  }

  manageAuthentication() {
    // remove Auth step if already authenticated
    if (this.isAuthenticated) {
      const updatedSteps = this.helpFlow.steps.filter((steps : any) => steps.type !== 'auth');
      this.helpFlow.steps = updatedSteps;
    } else if (this.helpFlow.activeStepIndex === 2){
      let helpContext = this.helpService.getHelpContext();
      // If user is on authentication step(activeStepIndex === 2)
      // If user is not authenticated redirect them to Auth0 login
      const navigationExtras: NavigationExtras = {
        queryParams: {
          partner: this.authService.getApiKey(),
          subject: helpContext.subject,
          intent: helpContext.intent,
          action: 'help_flow'
        }
      };
      this.router.navigate(['/login'], navigationExtras);
    }
  }

  checkChoiceEligibility(choice: any) {
    const hasRequiredPolicy = choice.requiresPolicy && this.policies && this.policies.length > 0;
    const hasRequiredActivePolicy = choice.requiresActivePolicy && this.hasActiveUpcomingPolicy(this.policies, ['active']);
    const hasRequiredActiveUpcomingPolicy = choice.requiresActiveUpcomingPolicy && this.hasActiveUpcomingPolicy(this.policies, ['active', 'upcoming']);
    const hasRequiredTravelPolicyWithSfd = choice.requiresTravelPolicyWithSfd && this.hasTravelPolicyWithSfd(this.policies);
    const hasRequiredClaim = choice.requiresClaim && this.claims && this.claims.length > 0;
    const hasNoSpecificNeeds = !choice.requiresPolicy && !choice.requiresActivePolicy && !choice.requiresActiveUpcomingPolicy && !choice.requiresClaim && !choice.requiresTravelPolicyWithSfd && !choice.requiresPaygNoOdo;

    return hasRequiredPolicy ||
      hasRequiredActivePolicy ||
      hasRequiredActiveUpcomingPolicy ||
      hasRequiredTravelPolicyWithSfd ||
      hasRequiredClaim ||
      hasNoSpecificNeeds;
  }
}
